/**
 * FreeModeStandings — persistent running scoreboard shown between rounds
 * in a mode=free room. Free mode returns to the waiting/grid view after
 * every round (the server never enters `roundEnd` for free play), so
 * without this the accumulated wins were invisible and each round felt
 * like it "ended abruptly and dumped you back to the lobby". This keeps a
 * constant tally of who's winning (ranked by lobbyScore = accumulated set
 * points) plus a one-line recap of the round that just finished.
 */
import type { LobbyView, RoundResult } from '@tt/lobby';
import type { GameTile } from '../../lib/api';
import { IconAvatar } from '../characters/IconAvatar';

interface Props {
  lobby: LobbyView;
  result: RoundResult | null;
  games: GameTile[];
  myCharId: string;
}

export function FreeModeStandings({ lobby, result, games, myCharId }: Props) {
  const players = lobby.players.filter((p) => !p.isSpectator);
  const ranking = [...players].sort(
    (a, b) => b.lobbyScore - a.lobbyScore || a.charId.localeCompare(b.charId),
  );
  const anyScore = ranking.some((p) => p.lobbyScore > 0);

  const lastGame = result
    ? (games.find((g) => g.id === result.gameId) ?? games.find((g) => g.baseGameId === result.gameId))
    : null;
  const winnerId = result?.ranks?.[0];
  const winner = winnerId ? players.find((p) => p.charId === winnerId) : null;
  const winnerDelta = winnerId ? (result?.lobbyDelta?.[winnerId] ?? 0) : 0;
  const hadWinner = !!winner && winnerDelta > 0;

  return (
    <section className="free-standings" aria-label="free play standings">
      <header className="free-standings-head">
        <h2 className="free-standings-title">Free play — running standings</h2>
        {result && (
          <p className="free-standings-last muted">
            Last round · {lastGame?.name ?? result.gameId} —{' '}
            {hadWinner ? <><strong>{winner!.name}</strong> won</> : 'no winner'}
          </p>
        )}
      </header>
      {anyScore ? (
        <ol className="free-standings-list">
          {ranking.map((p, i) => {
            const delta = result?.lobbyDelta?.[p.charId] ?? 0;
            const isMe = p.charId === myCharId;
            const isLeader = i === 0 && p.lobbyScore > 0;
            return (
              <li
                key={p.charId}
                className={`free-standings-row${isLeader ? ' is-leader' : ''}${isMe ? ' is-me' : ''}`}
              >
                <span className="free-standings-rank">{i + 1}</span>
                <IconAvatar icon={p.icon} size={28} alt={p.name} />
                <span className="free-standings-name">{p.name}</span>
                {delta > 0 && <span className="free-standings-delta">+{delta}</span>}
                <span className="free-standings-score">{p.lobbyScore}</span>
              </li>
            );
          })}
        </ol>
      ) : (
        <p className="muted free-standings-empty">No wins yet — launch a game to start the tally.</p>
      )}
    </section>
  );
}
